import type {
  HubspotSearchItem,
  HubspotSearchOwner
} from '@/features/assessments/api/hubspot-search'
import type { AssessmentFormValues } from '@/features/assessments/domain/assessment-form-schema'

export type HubspotPickFormValues = Partial<
  Pick<
    AssessmentFormValues,
    'plantName' | 'country' | 'dealOwnerUserID' | 'salesEngineerUserID' | 'hubspotPlantId'
  >
>

export type HubspotPickResult = {
  values: HubspotPickFormValues
  // The Deal ID for the "Pilot" phase created after submit; null for a plant pick.
  dealId: string | null
}

// Keys with no value are omitted so they never overwrite what the user typed.
export function hubspotPickToFormValues(pick: HubspotSearchItem): HubspotPickResult {
  const values: HubspotPickFormValues = {}
  const isDeal = pick.type === 'deal'

  const plantId = isDeal ? pick.plantHubspotId : pick.hubspotId
  if (plantId) values.hubspotPlantId = plantId

  const plantName = isDeal ? pick.plantTitle || pick.title : pick.title
  if (plantName) values.plantName = plantName

  if (pick.suggestedCountry) values.country = pick.suggestedCountry

  const dealOwnerUserID = scopeUserId(pick.dealOwner)
  if (dealOwnerUserID) values.dealOwnerUserID = dealOwnerUserID

  const salesEngineerUserID = scopeUserId(pick.salesEngineerOwner)
  if (salesEngineerUserID) values.salesEngineerUserID = salesEngineerUserID

  return { values, dealId: isDeal ? pick.hubspotId : null }
}

// The plant behind a pick: the item itself, or a deal's primary plant (only
// its ID and title are known). Null for a deal without a plant.
export function hubspotPickPlant(pick: HubspotSearchItem): HubspotSearchItem | null {
  if (pick.type === 'plant') return pick
  if (!pick.plantHubspotId) return null
  return {
    id: `plant:${pick.plantHubspotId}`,
    type: 'plant',
    hubspotId: pick.plantHubspotId,
    title: pick.plantTitle || pick.plantHubspotId,
    address: null,
    lastActivityAt: null,
    stageId: null,
    plantHubspotId: null,
    plantTitle: null,
    suggestedCountry: pick.suggestedCountry,
    dealOwner: null,
    salesEngineerOwner: null
  }
}

function scopeUserId(owner: HubspotSearchOwner | null): string | null {
  return owner?.userId || null
}
